import React, { useState, useEffect, useContext, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import {
  ErrorContext,
  SpinContext,
  JcTmpErrorContext,
  DatePickerContext,
  DataContext,
} from 'context'
import { dateCol, strCol, numCol } from '@integec/grid-tools/lib/cols'
import { modifierCol, modifiedDateCol } from 'core/HeaderColTypeProps'
import R from 'ramda'
import { CriteriaRow, Title, FullHeightSizer, Content } from 'core/screen-util'
// import CommonScreen from 'admin/component/CommonScreen'
import { solarPlantSelection, getSolarInverterGroup } from 'api/pgads'
// import {getSolarExpectedGeneration} from 'solar/solarapi'
import * as ScreenPermissions from 'core/EventActions'
import { hasAccesPermits } from 'core/utils'
import { getValidPlantList } from './util'
import {
  DatePicker,
  Select,
  message,
  Button,
  Tabs,
  Input,
  Checkbox,
  Tooltip,
} from 'antd'
import { isErrorContains } from 'core/component/JcTmpErrorDialog'
import {
  Grid,
  freeEditEngine,
  virtualizedGridRenderer,
} from '@integec/grid-tools'
import {
  rowHeaderRender,
  getApiTargetInfo,
  getPermitActions,
  exportDataComponent,
  ExportComponent,
} from 'core'
import {
  getSolarExpectedGeneration,
  updateSolarExpectedGeneration,
  updateSolarGrossGeneration,
  updateSolarNetGeneration,
} from './solarapi'
export const SolarGenerationDataHeader = selectedTab =>
  R.flatten([
    strCol({
      ident: 'plantId',
      display: 'Plant Id',
      isFiltered: true,
      sortOrder: 'asc',
      width: 250,
      isKey: true,
    }),
    ...(selectedTab === 'grossGen'
      ? [
          strCol({
            ident: 'inverterId',
            display: 'Inverter Id',
            isFiltered: true,
            sortOrder: 'asc',
            width: 150,
            isKey: true,
          }),
        ]
      : []),
    dateCol({
      ident: 'transDate',
      display: 'TransDate',
      isFiltered: true,
      sortOrder: 'asc',
      width: 150,
      isKey: true,
    }),
    R.range(1, 26).map(i =>
      numCol({ ident: `mwh${i}`, display: `MWH ${i}`, isFiltered: true })
    ),
    modifierCol,
    modifiedDateCol,
  ])

// const title = 'Solar Generation Data'
const { TabPane } = Tabs
const SolarGenerationData = ({ apiTargetInfo, actionPermits }) => {
  const [plantSelectionData, setPlantSelectionData] = useState([])
  const [plantList, setPlantList] = useState([])
  const [inverterGroupList, setInverterGroupList] = useState([])
  const { spinning, setSpinning } = useContext(SpinContext)
  const [expectedGendata, setExpectedGenData] = useState([])
  const [netGendata, setNetGenData] = useState([])
  const [grossGendata, setGrossGenData] = useState([])
  const {
    selectedSolarPlant,
    setSelectedSolarPlant: setPlantSelected,
  } = useContext(DataContext)
  const plantSelected = Array.isArray(selectedSolarPlant)
    ? selectedSolarPlant[0]
    : selectedSolarPlant
  const [selectedPlantEntity, setPlantEntitySelected] = useState()
  const [expectedGenFuzzyFilter, setExpectedGenFuzzyFilter] = useState('')
  const [netGenFuzzyFilter, setNetGenFuzzyFilter] = useState('')
  const [grossGenFuzzyFilter, setGrossGenFuzzyFilter] = useState('')
  const [populateData, setPopulateData] = useState('N')
  const [isPlantLocked, setIsPlantLocked] = useState(false)
  const [editInfo, setEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )
  const GridRef1 = useRef()
  const GridRef2 = useRef()
  const GridRef3 = useRef()
  const { setError } = useContext(ErrorContext)
  const { setTmpError } = useContext(JcTmpErrorContext)
  const [selectedTab, setSelectedTab] = useState('expectedGen')
  const { selectedDate, setSelectedDate } = useContext(DatePickerContext)

  const GENDATA_MAP = {
    expectedGen: {
      updateApi: updateSolarExpectedGeneration,
      data: expectedGendata,
      fuzzyFilter: expectedGenFuzzyFilter,
      setFuzzyFilter: setExpectedGenFuzzyFilter,
      title: 'Solar Expected Generation',
      ref: GridRef1,
    },
    netGen: {
      updateApi: updateSolarNetGeneration,
      data: netGendata,
      fuzzyFilter: netGenFuzzyFilter,
      setFuzzyFilter: setNetGenFuzzyFilter,
      title: 'Solar Net Generation',
      ref: GridRef2,
    },
    grossGen: {
      updateApi: updateSolarGrossGeneration,
      data: grossGendata,
      fuzzyFilter: grossGenFuzzyFilter,
      setFuzzyFilter: setGrossGenFuzzyFilter,
      title: 'Solar Gross Generation',
      ref: GridRef3,
    },
  }
  useEffect(() => {
    getSolarInverterGroup(apiTargetInfo).fork(setError, setInverterGroupList)
  }, [apiTargetInfo, setError])

  useEffect(
    _ => {
      solarPlantSelection(apiTargetInfo)({
        year: selectedDate.format('YYYY'),
        month: selectedDate.format('MM'),
      }).fork(setError, data => {
        setPlantList(data)
      })
    },
    [apiTargetInfo, selectedDate, setError]
  )

  useEffect(
    _ => {
      let stoList = []

      stoList = getValidPlantList(plantList, inverterGroupList, [])
      setPlantSelectionData(stoList)

      const isValidSelection = stoList.some(p => p.plantId === plantSelected)
      if (!isValidSelection && stoList[0] && stoList[0].plantId) {
        if (plantSelected !== stoList[0].plantId) {
          setPlantSelected([stoList[0].plantId])
        }
      }
    },
    [inverterGroupList, plantList, plantSelected, setPlantSelected]
  )

  const SolarGenerationDataWrite = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: apiTargetInfo.authGodMode,
        actionPermits,
        eventActions: [ScreenPermissions.SOLAR_GENERATION_DATA_WRITE],
        matchAny: true,
      }),
    [actionPermits, apiTargetInfo.authGodMode]
  )

  const toSourceNameMap = R.compose(
    R.fromPairs,
    R.map(u => [u.plantId, u.plantName || u.plantId])
  )

  const plantNameMap = toSourceNameMap(plantSelectionData)
  const headerWithFormatter = SolarGenerationDataHeader(selectedTab).map(
    header =>
      header.ident === 'plantId'
        ? {
            ...header,
            dataFormatter: ({ value }) =>
              value
                ? `${value}${
                    plantNameMap[value] ? `-${plantNameMap[value]}` : ''
                  }`
                : value,
          }
        : header
  )

  const beforeRetrieveValidation = () => {
    if (!plantSelected) {
      message.warning('Please, Select a Plant')
      return false
    }
    return true
  }

  const clearData = _ => {
    setExpectedGenData([])
    setNetGenData([])
    setGrossGenData([])
    setEditInfo(freeEditEngine.generateInitialEditInfo())
    setIsPlantLocked(false)
    setNetGenFuzzyFilter('')
    setExpectedGenFuzzyFilter('')
    setGrossGenFuzzyFilter('')
  }

  const retrieve = _ => {
    if (!beforeRetrieveValidation()) return

    setSpinning(true)
    getSolarExpectedGeneration(apiTargetInfo)({
      plant: plantSelected,
      entity: selectedPlantEntity,
      month: selectedDate.format('MM'),
      year: selectedDate.format('YYYY'),
      populateData,
    }).fork(setError, data => {
      setSpinning(false)
      setExpectedGenData(data?.expected)
      setNetGenData(data?.net)
      setGrossGenData(data?.gross)
      setIsPlantLocked(data?.lockStatus === 'L' || data?.lockStatus === 'l')
    })
  }

  const isEditableComp = ({ header }) => {
    const { ident } = header
    // if (!isEditable) return false
    if (
      ident === 'modifier' ||
      ident === 'modifiedDate' ||
      ident === 'plantId' ||
      ident === 'transDate'
    )
      return false
    return true
  }

  const editRow = editInfo => (data, setData, setEditInfo) => {
    const modData = data.map(d => {
      if (editInfo.updatedMap.get(d)) {
        return {
          ...editInfo.updatedMap.get(d),
          // modificationInfo: { user: null, modDate: moment() },
          modificationInfo: undefined,
          updated: true,
          tabSelected: selectedTab,
        }
      } else {
        return d
      }
    })

    let editData = editInfo.updated
    editData = editData.map(d => ({
      tabSelected: selectedTab,
      ...d,
      // modificationInfo: { user: null, modDate: moment() },
      modificationInfo: undefined,
    }))
    setData(modData)
    setEditInfo({ ...editInfo, updated: editData, tabSelected: selectedTab })
    setData(modData)
  }
  const updateData = _ => {
    const { data, updateApi } = GENDATA_MAP[selectedTab]
    const saveData = freeEditEngine.applyEdits({
      data: data.filter(d => d.updated && d.tabSelected === selectedTab),
      editInfo,
    })

    if (saveData.length <= 0) {
      message.error('Please, update data')
      return
    }

    setSpinning(true)

    updateApi(apiTargetInfo)(saveData).fork(setError, errorList => {
      setSpinning(false)
      setEditInfo(freeEditEngine.generateInitialEditInfo())
      setTmpError(errorList)
      if (!isErrorContains(errorList)) {
        message.info('Data Updated Successfully.')
        retrieve()
      }
    })
    setSpinning(false)
  }
  return (
    <div>
      <CriteriaRow>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '250px 250px 250px 150px 150px',
            gap: '10px',
            width: '100%',
          }}
        >
          <Title>Solar Generation Data</Title>
          <Select
            showSearch
            value={plantSelected}
            style={{ width: '100%' }}
            onChange={plant => {
              const selectedPlantData = plantSelectionData.find(
                p => p.plantId === plant
              )
              if (selectedPlantData) {
                setPlantSelected([selectedPlantData.plantId])
                setPlantEntitySelected(selectedPlantData.entityId)
              }
              clearData()
            }}
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
          >
            {plantSelectionData.map(({ plantName, plantId }) => (
              <Select.Option key={plantId} value={plantId}>
                {plantName}
              </Select.Option>
            ))}
          </Select>
          <DatePicker.MonthPicker
            style={{ marginLeft: '10px' }}
            allowClear={false}
            format="MMMM-YYYY"
            value={selectedDate}
            onChange={d => {
              setSelectedDate(d)
            }}
          />
          <Button
            icon="database"
            loading={spinning}
            onClick={() => retrieve()}
            style={{ marginLeft: '10px' }}
            type="primary"
          >
            Retrieve
          </Button>
          {SolarGenerationDataWrite && (
            <Tooltip title="When selected data will be populated using raw interface (PI) data.">
              <Checkbox
                style={{ padding: '5px 0px' }}
                checked={populateData === 'Y'}
                onChange={e => setPopulateData(e.target.checked ? 'Y' : 'N')}
                disabled={isPlantLocked}
                // disabled={!isImpactIsSequence || !(isRetrived && !isPlantLocked)}
              >
                Populate Data
              </Checkbox>
            </Tooltip>
          )}
        </div>
      </CriteriaRow>

      <FullHeightSizer heightOffset={200}>
        {({ height, width }) => (
          // data && data.length > 0 ? (
          <Content style={{ height: height, paddingTop: '0px' }}>
            <Tabs
              type="card"
              defaultActiveKey="netGen"
              onChange={setSelectedTab}
              style={{ height: 'inherit', paddingTop: '5px' }}
              activeKey={selectedTab}
              tabBarExtraContent={
                <div style={{ paddingRight: '50px', display: 'flex' }}>
                  <Button
                    icon="edit"
                    // loading={spinning}
                    disabled={
                      editInfo.updated.filter(
                        d => d.tabSelected === selectedTab
                      ).length === 0
                    }
                    onClick={() => {
                      setSpinning(true)
                      updateData()
                    }}
                    style={{ marginLeft: '10px', width: '200px' }}
                    type="primary"
                  >
                    Update
                  </Button>
                  <Input.Search
                    placeholder="Filter"
                    value={GENDATA_MAP[selectedTab].fuzzyFilter}
                    style={{ marginLeft: '10px', marginRight: '20px' }}
                    onChange={e =>
                      GENDATA_MAP[selectedTab].setFuzzyFilter(e.target.value)
                    }
                  />
                  <ExportComponent
                    getData={_ =>
                      exportDataComponent(
                        GENDATA_MAP[selectedTab].ref,
                        GENDATA_MAP[selectedTab].data,
                        GENDATA_MAP[selectedTab].headers
                      )
                    }
                    data={GENDATA_MAP[selectedTab].data}
                    headers={headerWithFormatter}
                    fileName={GENDATA_MAP[selectedTab].title}
                    btnStyle={{ marginRight: '10px' }}
                    disabled={
                      !!(
                        GENDATA_MAP[selectedTab].data &&
                        GENDATA_MAP[selectedTab].data.length <= 0
                      )
                    }
                  />
                </div>
              }
            >
              <TabPane
                tab="Solar Expected Generation"
                key="expectedGen"
                forceRender
              >
                <Grid
                  ref={GridRef1}
                  editMode="cell"
                  sortEnabled
                  data={expectedGendata}
                  headers={headerWithFormatter}
                  altBy={u => u.plantId}
                  render={virtualizedGridRenderer({
                    rowHeaderRender,
                    // autoFixColByKey: true,
                    height: height - 60,
                    width: width - 60,
                    selectionMode: 'single',
                    // headerRowHeight: 30,
                  })}
                  onEditInfoChange={info =>
                    editRow(info)(
                      expectedGendata,
                      setExpectedGenData,
                      setEditInfo
                    )
                  }
                  editInfo={editInfo}
                  fuzzyFilter={expectedGenFuzzyFilter}
                  isEditable={!isPlantLocked && isEditableComp}
                  selectionType="row"
                  selectionMode="single"
                  // initialSortOptions={SolarPiDataImportHeaders}
                />
              </TabPane>
              <TabPane tab="Solar Net Generation" key="netGen" forceRender>
                <Grid
                  ref={GridRef2}
                  editMode="cell"
                  sortEnabled
                  data={netGendata}
                  headers={headerWithFormatter}
                  altBy={u => u.plantId}
                  render={virtualizedGridRenderer({
                    rowHeaderRender,
                    // autoFixColByKey: true,
                    height: height - 60,
                    width: width - 60,
                    selectionMode: 'single',
                    // headerRowHeight: 30,
                  })}
                  onEditInfoChange={info =>
                    editRow(info)(netGendata, setNetGenData, setEditInfo)
                  }
                  editInfo={editInfo}
                  fuzzyFilter={netGenFuzzyFilter}
                  isEditable={!isPlantLocked && isEditableComp}
                  selectionType="row"
                  selectionMode="single"
                  // initialSortOptions={SolarPiDataImportHeaders}
                />
              </TabPane>
              <TabPane tab="Solar Gross Generation" key="grossGen" forceRender>
                <Grid
                  ref={GridRef3}
                  editMode="cell"
                  sortEnabled
                  data={grossGendata}
                  headers={headerWithFormatter}
                  altBy={u => u.plantId}
                  render={virtualizedGridRenderer({
                    rowHeaderRender,
                    // autoFixColByKey: true,
                    height: height - 60,
                    width: width - 60,
                    selectionMode: 'single',
                    // headerRowHeight: 30,
                  })}
                  onEditInfoChange={info =>
                    editRow(info)(grossGendata, setGrossGenData, setEditInfo)
                  }
                  editInfo={editInfo}
                  fuzzyFilter={grossGenFuzzyFilter}
                  isEditable={!isPlantLocked && isEditableComp}
                  selectionType="row"
                  selectionMode="single"
                  // initialSortOptions={SolarPiDataImportHeaders}
                />
              </TabPane>
            </Tabs>
          </Content>
        )}
      </FullHeightSizer>
    </div>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  actionPermits: getPermitActions(state),
})

export default connect(mapStateToProps)(SolarGenerationData)
